import { useState, useEffect, useRef, useMemo } from "react";
import {
  collection,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  setDoc,
  query,
  where,
  limit,
  startAfter,
  getCountFromServer,
  GeoPoint,
  orderBy,
  startAt,
  endAt,
  getDoc,
} from "firebase/firestore";
import { db } from "../utils/firebase-config"; // Adjust the import according to your project structure
import { useNotification } from "../components/Notification";
import { useAppState } from "../appContext";
import { GeoFirestore } from "geofirestore"; // Ensure correct import path
import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import { FieldPath } from "firebase/firestore";
import { 
  formatForDatabase, 
  normalizeDate, 
  getEventBufferCutoff, 
  compareEventsByDate,
  isUpcoming 
} from "../utils/dateUtils";
import { geohashQueryBounds, distanceBetween } from "geofire-common";
import { LRUCache } from "lru-cache";
import { useQuery } from "@tanstack/react-query";
import { eventKeys, pageKeys } from "../lib/queryClient";

// Initialize GeoFirestore
const geoFirestore = new GeoFirestore(db);
const currentDate = new Date();
const fourHoursBefore = new Date(Date.now() - 4 * 60 * 60 * 1000);

const parseQueryString = (field, operator, value) => {
  switch (operator) {
    case "==":
      return where(field, "==", value);
    case "!=":
      return where(field, "!=", value);
    case "<":
      return where(field, "<", value);
    case "<=":
      return where(field, "<=", value);
    case ">":
      return where(field, ">", value);
    case ">=":
      return where(field, ">=", value);
    case "array-contains":
      return where(field, "array-contains", value);
    case "in":
      return where(field, "in", value);
    case "array-contains-any":
      return where(field, "array-contains-any", value);
    default:
      throw new Error(`Unsupported query operator: ${operator}`);
  }
};

const fetchVenueIdsByFilters = async (filters) => {
  let venueQuery = collection(db, "venues");
  filters.forEach(({ field, operator, value }) => {
    const whereClause = parseQueryString(field, operator, value);
    venueQuery = query(venueQuery, whereClause);
  });

  const venueSnapshot = await getDocs(venueQuery);
  const ids = venueSnapshot.docs.map((doc) => doc.id);
  return ids;
};

const chunkArray = (array, size) => {
  const chunks = [];
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }
  return chunks;
};

const sortEvents = (events) => {
  // Separate future/current events and past events using the new date utilities
  const futureEvents = events.filter((event) => isUpcoming(event.main_date));
  const pastEvents = events.filter((event) => !isUpcoming(event.main_date));

  // Sort future/current events in ascending order
  futureEvents.sort(compareEventsByDate);

  // Sort past events in descending order (reverse comparison)
  pastEvents.sort((a, b) => -compareEventsByDate(a, b));

  // Combine the sorted arrays
  return [...futureEvents, ...pastEvents];
};

// LRU cache with size limits and TTL to prevent unbounded growth
const queryCache = new LRUCache({
  max: 100,              // Maximum 100 entries
  ttl: 1000 * 60 * 5,    // 5 minutes TTL
  updateAgeOnGet: true,  // Refresh TTL on access
});

const generateCacheKey = (geoFilter, filters) => {
  const { miles, latitude, longitude } = geoFilter.value[0];
  const filterKey = filters
    ?.map((f) => `${f.field}-${f.operator}-${f.value}`)
    .join("-");
  return `${latitude}-${longitude}-${miles}-${filterKey}`;
};

const fetchGeoFilteredRecords = async (geoFilter, take = null, lastDoc = null) => {
  // Extract coordinates from geoFilter
  const { miles, latitude, longitude } = geoFilter.value[0];
  
  const cacheKey = generateCacheKey(geoFilter, []);
  
  // Check if results for this query are already cached
  if (queryCache.has(cacheKey)) {
    return queryCache.get(cacheKey);
  }

  try {
    // Build query parameters for the API with reduced limit
    const params = new URLSearchParams({
      lat: latitude.toString(),
      lon: longitude.toString(),
      radius: miles.toString(),
      limit: "100", // Capped at 100 to prevent 4MB response size limit
    });

    // Call the API with timeout
    const apiUrl = `/api/events/nearby?${params.toString()}`;
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 8000); // 8 second timeout
    
    const response = await fetch(apiUrl, { 
      signal: controller.signal,
      headers: {
        'Accept': 'application/json',
      }
    });
    
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`API request failed: ${response.status}`, errorText);
      throw new Error(`API request failed: ${response.status}`);
    }

    // Validate content type
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      console.error('Invalid response content type:', contentType);
      throw new Error('Invalid response format from API');
    }

    const data = await response.json();

    // Validate response structure
    if (!data || !Array.isArray(data.events)) {
      console.error('Invalid API response structure:', data);
      throw new Error('Invalid response structure from API');
    }

    // Convert API response to match expected format
    const matchingDocs = data.events.map(event => ({
      ...event,
      // Convert ISO strings back to Firestore-like objects for compatibility
      main_date: {
        toDate: () => new Date(event.main_date),
        seconds: new Date(event.main_date).getTime() / 1000,
      },
      modifieddate: event.modifieddate ? {
        toDate: () => new Date(event.modifieddate),
        seconds: new Date(event.modifieddate).getTime() / 1000,
      } : null,
    }));

    const result = {
      docs: matchingDocs,
      totalCount: data.pagination.total,
      isPaginated: false,
    };

    // Cache the result
    queryCache.set(cacheKey, result);

    return result;
  } catch (error) {
    // Log detailed error information
    if (error.name === 'AbortError') {
      console.error("API request timed out after 8 seconds");
    } else if (error instanceof SyntaxError) {
      console.error("JSON parsing error - API response may be too large or malformed:", error.message);
    } else {
      console.error("Error fetching geo-filtered events from API:", error);
    }
    
    // Fallback to empty result on error
    const result = { docs: [], totalCount: 0, isPaginated: false };
    return result;
  }
};

const fetchStandardFilteredRecords = async (filters, take, lastDoc, db) => {
  let orderByField = "main_date";
  const inequalities = ["<", "<=", "!=", "not-in", ">", ">="];
  let eventQuery = null;
  eventQuery = collection(db, "events");

  const hasInEquality =
    filters.filter((f) => inequalities.includes(f.operator)).length > 0;
  const hasSearchFilter =
    filters.filter((f) => f.field === "title_lowercase").length > 0;
  if (hasSearchFilter) {
    orderByField = "title_lowercase";
  }

  for (const { field, operator, value } of filters) {
    const parsedQuery = parseQueryString(field, operator, value);
    eventQuery = query(eventQuery, parseQueryString(field, operator, value));
  }
  if (!hasInEquality) {
    eventQuery = query(eventQuery, where("main_date", ">=", fourHoursBefore));
  }
  const countSnapshot = await getCountFromServer(eventQuery);
  //  eventQuery = query(eventQuery, orderBy(orderByField, "asc"), limit(take));
  eventQuery = query(eventQuery, limit(take));
  if (lastDoc) {
    eventQuery = query(eventQuery, startAfter(lastDoc));
  }

  const querySnapshot = await getDocs(eventQuery);
  const docs = querySnapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));
  const lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1];

  const totalCount = countSnapshot.data().count;
  return { docs, totalCount, lastVisible, isPaginated: true };
};
// LRU cache for combined queries with size limits and TTL to prevent unbounded growth
const combinedQueryCache = new LRUCache({
  max: 50,               // Maximum 50 entries (smaller - more complex queries)
  ttl: 1000 * 60 * 5,    // 5 minutes TTL
  updateAgeOnGet: true,  // Refresh TTL on access
});

const generateCombinedCacheKey = (geoFilter, filters) => {
  const { miles, latitude, longitude } = geoFilter.value[0];
  const filterKey = filters
    .map((f) => `${f.field}-${f.operator}-${f.value}`)
    .join("-");
  return `${latitude}-${longitude}-${miles}-${filterKey}`;
};

const fetchCombinedFilteredRecords = async (geoFilter, filters, db) => {
  // Generate a cache key for the combined query
  const combinedCacheKey = generateCombinedCacheKey(geoFilter, filters);

  // Check if the combined results for this query are already cached
  if (combinedQueryCache.has(combinedCacheKey)) {
    return combinedQueryCache.get(combinedCacheKey);
  }

  // First, get the geospatial results using the cached fetchGeoFilteredRecords function
  const geoResults = await fetchGeoFilteredRecords(geoFilter);

  const geoDocIds = new Set(geoResults.docs.map((doc) => doc.id)); // Use a Set for faster lookup

  // Apply the standard filters and get all matching documents
  let eventQuery = collection(db, "events");
  const inequalities = ["<", "<=", "!=", "not-in", ">", ">="];
  const hasInEquality =
    filters.filter((f) => inequalities.includes(f.operator)).length > 0;

  for (const { field, operator, value } of filters) {
    eventQuery = query(eventQuery, parseQueryString(field, operator, value));
  }
  // if(!hasInEquality){
  //   eventQuery=query(eventQuery,where("main_date", ">=", new Date()))

  // }
  eventQuery = query(eventQuery, orderBy("main_date", "asc"));

  const querySnapshot = await getDocs(eventQuery);
  const allStandardFilteredDocs = querySnapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));

  // Intersect the standard filtered results with the geospatial results
  const matchingDocs = allStandardFilteredDocs.filter((doc) =>
    geoDocIds.has(doc.id)
  );
  const totalCount = matchingDocs.length;
  const result = { docs: matchingDocs, totalCount, isPaginated: false };

  // Cache the combined result
  combinedQueryCache.set(combinedCacheKey, result);

  return result;
};

// Extract fetch logic into a standalone async function
const fetchEventsData = async ({ take, skip, filters, ignoreFilters, searchTerm }) => {
  let result;

  if (ignoreFilters) {
    // If ignoring filters, we only want to apply the search term.
    const searchFilter = searchTerm ? [{
      field: "searchTerms",
      operator: "array-contains-any",
      value: searchTerm.toLowerCase().split(" ")
    }] : [];
    
    result = await fetchStandardFilteredRecords(searchFilter, take, null, db);
  } else {
    // Use the provided filters.
    const geospatialFilter = filters.filter(
      (f) => f.field === "coordinates"
    );
    const standardFilters = filters.filter(
      (f) => f.field !== "coordinates"
    );

    if (geospatialFilter.length > 0 && standardFilters.length === 0) {
      result = await fetchGeoFilteredRecords(
        geospatialFilter[0],
        take,
        null
      );
    } else if (geospatialFilter.length === 0) {
      result = await fetchStandardFilteredRecords(
        standardFilters,
        take,
        null,
        db
      );
    } else if (geospatialFilter.length > 0 && standardFilters.length > 0) {
      result = await fetchCombinedFilteredRecords(
        geospatialFilter[0],
        standardFilters,
        db
      );
    }
  }

  // Process and format the events
  const processedDocs = result.docs.map((d) => ({
    ...d,
    main_date: formatForDatabase(d.main_date),
    modifieddate: formatForDatabase(d.modifieddate),
  }));

  // If the API already paginated, return just those results
  // If not paginated, return ALL events so the component can handle client-side pagination
  const eventsToReturn = result.isPaginated 
    ? processedDocs 
    : processedDocs; // Return ALL events for client-side pagination

  return {
    events: sortEvents(eventsToReturn),
    totalCount: result.totalCount,
    isPaginated: result.isPaginated,
    allDocs: processedDocs, // For local pagination
  };
};

export const useFetchEvents = (
  take = 20,
  skip = 0,
  filters = [],
  enableApi = false,
  ignoreFilters = false,
  searchTerm = "" 
) => {
  // Create a stable query key
  const queryKey = useMemo(
    () => eventKeys.list({ take, skip, filters, ignoreFilters, searchTerm }),
    [take, skip, filters, ignoreFilters, searchTerm]
  );

  // Use React Query for data fetching
  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: () => fetchEventsData({ take, skip, filters, ignoreFilters, searchTerm }),
    enabled: enableApi,
    staleTime: 5 * 60 * 1000, // 5 minutes - matches previous cache TTL
    // Prevent automatic refetching on mount/focus/reconnect
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // Return data in the same format as before for backward compatibility
  return {
    events: data?.events || [],
    loading: isLoading,
    totalItems: data?.totalCount || 0,
    isPaginated: data?.isPaginated ?? true,
    searchTerm,
  };
};
// End new fetch evemts
const fetchEventsByVenueIdData = async (venueId, showPastEvents) => {
  const eventsCollection = collection(db, "events");
  let q;
  if (showPastEvents) {
    q = query(
      collection(db, "events"),
      where("venue", "==", venueId),
      orderBy("main_date", "asc")
    );
  } else {
    q = query(
      collection(db, "events"),
      where("venue", "==", venueId),
      orderBy("main_date", "asc"),
      where("main_date", ">=", fourHoursBefore)
    );
  }

  const querySnapshot = await getDocs(q);

  const eventsList = querySnapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));
  
  const processedEvents = sortEvents(
    eventsList.map((d) => ({
      ...d,
      main_date: formatForDatabase(d.main_date),
      modifieddate: formatForDatabase(d.modifieddate),
    }))
  );
  
  return {
    events: processedEvents,
    totalItems: querySnapshot.size,
  };
};

export const useFetchEventsByVenueId = (venue, showPastEvents) => {
  const { data, isLoading } = useQuery({
    queryKey: eventKeys.byVenue(venue, showPastEvents),
    queryFn: () => fetchEventsByVenueIdData(venue, showPastEvents),
    enabled: !!venue,
    staleTime: 5 * 60 * 1000,
  });

  return {
    events: data?.events || [],
    loading: isLoading,
    totalItems: data?.totalItems || 0,
  };
};

const fetchEventByIdData = async (eventId) => {
  const eventDocRef = doc(db, "events", eventId);
  const eventDoc = await getDoc(eventDocRef);

  if (!eventDoc.exists()) {
    throw new Error("Event not found");
  }

  const eventData = eventDoc.data();

  return {
    id: eventDoc.id,
    ...eventData,
    main_date: formatForDatabase(eventData.main_date),
    modifieddate: formatForDatabase(eventData.modifieddate),
  };
};

export const useFetchEventById = (eventId) => {
  const { data, isLoading, error } = useQuery({
    queryKey: eventKeys.detail(eventId),
    queryFn: () => fetchEventByIdData(eventId),
    enabled: !!eventId,
    staleTime: 5 * 60 * 1000,
  });

  return {
    event: data || null,
    loading: isLoading,
    error: error ? error.message : null,
  };
};

const fetchEventsByBandIdData = async (bandId, showPastEvents) => {
  const eventsCollection = collection(db, "events");
  let q;
  if (showPastEvents) {
    q = query(
      eventsCollection,
      where("bandIds", "array-contains", bandId),
      orderBy("main_date", "asc")
    );
  } else {
    q = query(
      eventsCollection,
      where("bandIds", "array-contains", bandId),
      where("main_date", ">=", fourHoursBefore),
      orderBy("main_date", "asc")
    );
  }

  const querySnapshot = await getDocs(q);

  const eventsList = querySnapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));
  
  const processedEvents = sortEvents(
    eventsList.map((d) => ({
      ...d,
      main_date: formatForDatabase(d.main_date),
      modifieddate: formatForDatabase(d.modifieddate),
    }))
  );
  
  return {
    events: processedEvents,
    totalItems: querySnapshot.size,
  };
};

export const useFetchEventsByBandId = (band, showPastEvents) => {
  const { data, isLoading } = useQuery({
    queryKey: eventKeys.byBand(band, showPastEvents),
    queryFn: () => fetchEventsByBandIdData(band, showPastEvents),
    enabled: !!band,
    staleTime: 5 * 60 * 1000,
  });

  return {
    events: data?.events || [],
    loading: isLoading,
    totalItems: data?.totalItems || 0,
  };
};

const fetchPageByInternalLinkData = async (internallink) => {
  if (!internallink) {
    throw new Error("Internal link is required");
  }

  const pagesRef = collection(db, "pages");
  const q = query(
    pagesRef,
    where("internallink", "==", `/${internallink.toLowerCase()}/`)
  );
  const querySnapshot = await getDocs(q);

  if (querySnapshot.empty) {
    throw new Error("Page not found");
  }

  const pageDoc = querySnapshot.docs[0];
  return {
    id: pageDoc.id,
    ...pageDoc.data(),
  };
};

export const useFetchPageByInternalLink = (internallink) => {
  const { data, isLoading, error } = useQuery({
    queryKey: pageKeys.byInternalLink(internallink),
    queryFn: () => fetchPageByInternalLinkData(internallink),
    enabled: !!internallink,
    staleTime: 5 * 60 * 1000,
  });

  return {
    page: data || null,
    loading: isLoading,
    error: error ? error.message : null,
  };
};
